import { NavigationContext, useNavigationContext } from "./context.js";
import { activateNode, composeHandlers, createNode, focusNode, insertNode, isFocused, joinId, registerFocusListener, removeNode, updateNode } from "@fiveway/core";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/node.tsx
const NULL_NODE = {};
/**
* React hook that creates a navigation node and returns a handle for it.
* When creating container nodes, returned `Context` must be used to provide this node as parent to children nodes.
*
* @param id - Local ID of the node.
* @param handler - The handler for the node. Either a single handler or an array of handlers. If not provided, the node will use the `defaultHandler`.
* @param options - The options for the node.
*
* @see {@link NavnodeOptions}
* @see {@link NavnodeHandle}
*
* @example
* ```tsx
* const nav = useNavnode("layout", containerHandler);
* return (
*   <nav.Context>
*     <Sidebar/>
*     <Content />
*     <Footer />
*   </nav.Context>
* );
* ```
*/
function useNavnode(id, handler, options = {}) {
	const { tree, parentNode } = useNavigationContext();
	const parent = options.parent ?? parentNode;
	handler = Array.isArray(handler) ? composeHandlers(handler) : handler;
	const nodeRef = useRef(NULL_NODE);
	if (nodeRef.current === NULL_NODE || nodeRef.current.id !== id || nodeRef.current.parent !== parent) nodeRef.current = createNode({
		id,
		parent,
		handler,
		order: options.order
	});
	else updateNode(nodeRef.current, {
		handler,
		order: options.order
	});
	const nodeId = nodeRef.current.id;
	useEffect(() => {
		insertNode(tree, nodeRef.current);
		return () => {
			removeNode(tree, nodeId);
		};
	}, [tree, nodeId]);
	const isFocused = useLazyIsFocused(tree, nodeId);
	const focus = (target, options) => {
		const id = target != null ? joinId(nodeId, target) : nodeId;
		focusNode(tree, id, options);
	};
	const activate = (target, options) => {
		const id = target != null ? joinId(nodeId, target) : nodeId;
		activateNode(tree, id, options);
	};
	const Context = useCallback((props) => {
		const context = {
			tree,
			parentNode: nodeId
		};
		return /* @__PURE__ */ jsx(NavigationContext.Provider, {
			value: context,
			children: props.children
		});
	}, [tree, nodeId]);
	Context.displayName = "NodeContext";
	return {
		id: nodeId,
		isFocused,
		focus,
		activate,
		Context
	};
}
/**
* React component version of {@link useNavnode}. It takes same options {@link NavnodeOptions} and creates a navigation node.
* It provides correct navigation context to children so you don't have to use `nav.Context` manually.
* It can be given function that accepts {@link NavnodeHandle} as children.
*
* @see {@link NavnodeProps}
*
* @example
* ```tsx
* <Navnode id="layout" handler={containerHandler}>
*   <Sidebar/>
*   <Content />
*   <Footer />
* </Navnode>
* ```
*/
function Navnode({ children, ...props }) {
	const nav = useNavnode(props.id, props.handler, props);
	return /* @__PURE__ */ jsx(nav.Context, { children: typeof children === "function" ? children(nav) : children });
}
function useLazyIsFocused(tree, nodeId) {
	const [subscribed, setSubscribed] = useState(false);
	const subscribe = useCallback((handler) => registerFocusListener(tree, nodeId, handler), [tree, nodeId]);
	const subscribedValue = useSyncExternalStore(subscribed ? subscribe : noopSubscribe, () => isFocused(tree, nodeId));
	return () => {
		if (!subscribed) setSubscribed(true);
		return subscribedValue;
	};
}
function noopSubscribe() {
	return () => {};
}
//#endregion
export { Navnode, useNavnode };

//# sourceMappingURL=node.js.map